// Tiny WebAudio sound kit. Riffle + card deal use the user's recorded clips (public/assets/sfx); the rest is synthesized.
// Honours the Sounds setting.
let ctx;
const enabled = () => localStorage.getItem("pfp_sound") !== "off" && !document.hidden; // a background tab stays silent: no burst of queued clinks on return
const ac = () => {
  ctx ||= new (window.AudioContext || window.webkitAudioContext)();
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
};

const CLIPS = ["riffle", "deal", "chips", "raise", "allin", "turn"];
const buffers = {}; // name -> AudioBuffer once decoded, Promise while loading, "failed" if the clip is unavailable
const load = (name) => (buffers[name] ||= fetch(`/assets/sfx/${name}.mp3`).then((r) => r.arrayBuffer()).then((b) => ac().decodeAudioData(b))
  .then((buf) => (buffers[name] = buf)).catch(() => (buffers[name] = "failed")));
export const preloadSounds = () => Promise.all(CLIPS.map(load)).catch(() => {});
// decoded clip, or null. Kicks the load if it isn't in flight (e.g. after a hot reload wiped module state).
const ready = (name) => { if (!buffers[name]) load(name); return buffers[name] instanceof AudioBuffer ? buffers[name] : null; };
// the synthesized fallback is only for when a clip truly failed to load — never while it's still decoding
const fallbackOk = (name) => buffers[name] === "failed";

const slice = (buf, { start, dur, gain = 1, rate = 1 }, at = 0) => {
  const a = ac();
  const src = a.createBufferSource();
  src.buffer = buf; src.playbackRate.value = rate;
  const g = a.createGain();
  const t = a.currentTime + at;
  g.gain.setValueAtTime(gain, t); g.gain.setValueAtTime(gain, t + dur - 0.03); g.gain.linearRampToValueAtTime(0, t + dur);
  src.connect(g).connect(a.destination);
  src.start(t, start, dur);
};
// single-card hits inside the "cards being dealt" recording (start s, normalising gain)
const DEAL_HITS = [{ start: 0.85, gain: 3.9 }, { start: 1.97, gain: 3.8 }, { start: 6.33, gain: 5.9 }, { start: 20.62, gain: 1.8 }];
export const RIFFLE_CLIP = { start: 0.25, dur: 1.65 }; // the riffle itself, without the trailing squaring-up tap

const noise = (dur, { freq = 2500, q = 0.8, gain = 0.5, attack = 0.004 } = {}, at = 0) => {
  const a = ac();
  const buf = a.createBuffer(1, Math.ceil(a.sampleRate * dur), a.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
  const src = a.createBufferSource();
  src.buffer = buf;
  const f = a.createBiquadFilter();
  f.type = "bandpass"; f.frequency.value = freq; f.Q.value = q;
  const g = a.createGain();
  const t = a.currentTime + at;
  g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(gain, t + attack); g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  src.connect(f).connect(g).connect(a.destination);
  src.start(t);
};

const tone = (freq, dur, gain, at = 0) => {
  const a = ac();
  const o = a.createOscillator();
  o.type = "triangle"; o.frequency.value = freq;
  const g = a.createGain();
  const t = a.currentTime + at;
  g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(gain, t + 0.002); g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  o.connect(g).connect(a.destination);
  o.start(t); o.stop(t + dur + 0.01);
};

// one chip clink = short bright ping + tiny click of noise
const clink = (at, pitch = 1, gain = 0.18) => {
  tone(3900 * pitch, 0.07, gain, at);
  tone(6200 * pitch, 0.04, gain * 0.45, at + 0.003);
  noise(0.02, { freq: 5000, q: 0.6, gain: gain * 0.6 }, at);
};

export const sounds = {
  // chips slid in (bet / call): the user's "placing poker chips" clip; synth fallback until it's decoded
  chipsBet() {
    if (!enabled()) return;
    const buf = ready("chips");
    if (buf) return slice(buf, { start: 0, dur: 0.4, gain: 0.9, rate: 0.97 + Math.random() * 0.06 });
    if (!fallbackOk("chips")) return;
    noise(0.22, { freq: 700, q: 0.4, gain: 0.12 });
    for (let i = 0; i < 5; i++) clink(0.05 + i * 0.045 + Math.random() * 0.012, 0.95 + Math.random() * 0.12);
  },
  // raise: the user's second "placing chips" clip (a heavier stack); synth fallback until decoded
  chipsRaise() {
    if (!enabled()) return;
    const buf = ready("raise");
    if (buf) return slice(buf, { start: 0, dur: 0.45, gain: 0.9, rate: 0.97 + Math.random() * 0.06 });
    if (!fallbackOk("raise")) return;
    noise(0.32, { freq: 600, q: 0.4, gain: 0.16 });
    for (let i = 0; i < 11; i++) clink(0.04 + i * 0.038 + Math.random() * 0.01, 0.82 + Math.random() * 0.14, 0.2);
    noise(0.05, { freq: 1200, q: 0.9, gain: 0.3 }, 0.5);
    tone(2600, 0.06, 0.15, 0.5);
  },
  // all-in: the user's "all in push chips" clip
  chipsAllIn() {
    if (!enabled()) return;
    const buf = ready("allin");
    if (buf) return slice(buf, { start: 0.08, dur: 0.6, gain: 0.85 });
    if (!fallbackOk("allin")) return;
    sounds.chipsRaise();
  },
  riffle() {
    if (!enabled()) return;
    const buf = ready("riffle");
    if (buf) return slice(buf, { ...RIFFLE_CLIP, gain: 0.9 });
    if (!fallbackOk("riffle")) return;
    for (let i = 0; i < 26; i++) noise(0.035, { freq: 1800 + Math.random() * 1500, q: 1.5, gain: 0.25 }, i * 0.028);
    noise(0.25, { freq: 900, q: 0.5, gain: 0.2 }, 0.75);
  },
  // jackpot hit: long coin cascade under a rising fanfare arpeggio
  jackpot() {
    if (!enabled()) return;
    noise(1.6, { freq: 700, q: 0.4, gain: 0.16 });
    for (let i = 0; i < 44; i++) clink(0.05 + i * 0.04 + Math.random() * 0.01, 0.8 + Math.random() * 0.4, 0.16);
    [523, 659, 784, 1047, 1319, 1568, 2093].forEach((f, i) => tone(f, 0.5, 0.14, 0.12 + i * 0.14));
    tone(1047, 1.2, 0.12, 1.2); tone(1319, 1.2, 0.1, 1.2); tone(1568, 1.4, 0.1, 1.2);
  },
  // pot pushed to the winner: a long chip cascade under a bright two-note chime
  win() {
    if (!enabled()) return;
    noise(0.5, { freq: 650, q: 0.4, gain: 0.14 });
    for (let i = 0; i < 16; i++) clink(0.05 + i * 0.045 + Math.random() * 0.01, 0.85 + Math.random() * 0.2, 0.17);
    tone(880, 0.35, 0.16, 0.15); tone(1320, 0.45, 0.14, 0.32); tone(1760, 0.5, 0.1, 0.5);
  },
  // action passes to the next player: soft tick; the user's chime clip when it's you
  turn(mine = false) {
    if (!enabled()) return;
    if (mine) {
      const buf = ready("turn");
      if (buf) return slice(buf, { start: 0, dur: 1.4, gain: 2.5 });
      if (!fallbackOk("turn")) return;
      tone(1047, 0.12, 0.18); tone(1568, 0.22, 0.16, 0.11); return;
    }
    tone(740, 0.08, 0.1); noise(0.03, { freq: 3000, q: 1, gain: 0.08 });
  },
  // fold: a soft card slide back to the dealer (quieter, lower deal hit)
  fold() {
    if (!enabled()) return;
    const buf = ready("deal");
    if (buf) return slice(buf, { ...DEAL_HITS[1], dur: 0.22, gain: DEAL_HITS[1].gain * 0.6, rate: 0.85 });
    if (!fallbackOk("deal")) return;
    noise(0.12, { freq: 1800, q: 0.6, gain: 0.2 });
  },
  // shot clock: beep each of the last 5 seconds, sharper for the final two
  beep(urgent = false) {
    if (!enabled()) return;
    tone(urgent ? 1320 : 990, 0.09, urgent ? 0.3 : 0.22);
    if (urgent) tone(1320, 0.09, 0.3, 0.14);
  },
  deal(at = 0) {
    if (!enabled()) return;
    const buf = ready("deal");
    if (buf) return slice(buf, { ...DEAL_HITS[Math.floor(Math.random() * DEAL_HITS.length)], dur: 0.22, rate: 0.95 + Math.random() * 0.1 }, at);
    if (!fallbackOk("deal")) return;
    noise(0.09, { freq: 3200, q: 0.7, gain: 0.35 }, at);
  },
  // button press: short mechanical click (a tap of filtered noise with a tiny low thump)
  click() { if (enabled()) { noise(0.025, { freq: 3200, q: 1.2, gain: 0.35 }); tone(180, 0.04, 0.12); } },
  flip(at = 0) { if (enabled()) { noise(0.06, { freq: 2200, q: 1, gain: 0.3 }, at); noise(0.05, { freq: 600, q: 0.8, gain: 0.25 }, at + 0.04); } },
};
